import React, { useState, useEffect } from "react";
import { Button } from "../../../../design/button";
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogTitle,
} from "../../../../design/dialog";
import { Field, Fieldset, Label, ErrorMessage } from "../../../../design/fieldset";
import { Input } from "../../../../design/input";
import { Select } from "../../../../design/select";
import { Textarea } from "../../../../design/textarea";
import { Checkbox, CheckboxField } from "../../../../design/checkbox";
import {
  CaseManagementEvent,
  CaseManagementEventType,
  ContactMethod,
  CASE_MANAGEMENT_EVENT_TYPE_LABELS,
  CONTACT_METHOD_LABELS,
  CreateCaseManagementEventRequest,
  UpdateCaseManagementEventRequest,
} from "@visitproof/types";
import moment from "moment";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: CreateCaseManagementEventRequest | Omit<UpdateCaseManagementEventRequest, "eventId">) => Promise<void>;
  caseId: string;
  event?: CaseManagementEvent | null;
}

interface FormErrors {
  eventType?: string;
  contactMethod?: string;
  summary?: string;
  occurredAt?: string;
  submit?: string;
}

const AddCaseManagementEventDialog: React.FC<Props> = ({
  isOpen,
  onClose,
  onSave,
  caseId,
  event,
}) => {
  const isEditing = !!event;

  const [eventType, setEventType] = useState<CaseManagementEventType>("client_communication");
  const [contactMethod, setContactMethod] = useState<ContactMethod | "">("");
  const [summary, setSummary] = useState("");
  const [notes, setNotes] = useState("");
  const [outcome, setOutcome] = useState("");
  const [followUpNeeded, setFollowUpNeeded] = useState(false);
  const [followUpDate, setFollowUpDate] = useState("");
  const [occurredAt, setOccurredAt] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [errors, setErrors] = useState<FormErrors>({});

  // Reset form when dialog opens - only reset when dialog first opens, not on every event change
  useEffect(() => {
    if (isOpen) {
      if (event) {
        setEventType(event.eventType);
        setContactMethod(event.contactMethod || "");
        setSummary(event.summary);
        setNotes(event.notes || "");
        setOutcome(event.outcome || "");
        setFollowUpNeeded(event.followUpNeeded);

        // Handle occurredAt date safely — may be a Timestamp, a serialized
        // {_seconds,_nanoseconds} (from httpsCallable), a string, or a Date.
        try {
          const raw: any = event.occurredAt;
          const occurredDate = raw?.toDate?.()
            ?? (raw?._seconds != null ? new Date(raw._seconds * 1000) : new Date(raw));
          const formattedDate = moment(occurredDate);
          if (formattedDate.isValid()) {
            setOccurredAt(formattedDate.format("YYYY-MM-DDTHH:mm"));
          } else {
            setOccurredAt(moment().format("YYYY-MM-DDTHH:mm"));
          }
        } catch {
          setOccurredAt(moment().format("YYYY-MM-DDTHH:mm"));
        }

        // Handle followUpDate safely
        if (event.followUpDate) {
          try {
            const followUp = event.followUpDate?.toDate?.() ?? new Date(event.followUpDate as any);
            const formattedFollowUp = moment(followUp);
            if (formattedFollowUp.isValid()) {
              setFollowUpDate(formattedFollowUp.format("YYYY-MM-DD"));
            } else {
              setFollowUpDate("");
            }
          } catch {
            setFollowUpDate("");
          }
        } else {
          setFollowUpDate("");
        }
      } else {
        // New event defaults
        setEventType("client_communication");
        setContactMethod("");
        setSummary("");
        setNotes("");
        setOutcome("");
        setFollowUpNeeded(false);
        setFollowUpDate("");
        setOccurredAt(moment().format("YYYY-MM-DDTHH:mm"));
      }
      setErrors({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const validate = (): boolean => {
    const newErrors: FormErrors = {};

    if (!eventType) {
      newErrors.eventType = "Event type is required";
    }

    if (eventType === "client_communication" && !contactMethod) {
      newErrors.contactMethod = "Contact method is required for communication events";
    }

    if (!summary.trim()) {
      newErrors.summary = "Summary is required";
    }

    if (!occurredAt) {
      newErrors.occurredAt = "Date and time is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    setIsSaving(true);
    setErrors((prev) => ({ ...prev, submit: undefined }));
    try {
      if (isEditing) {
        const updateData: Omit<UpdateCaseManagementEventRequest, "eventId"> = {
          eventType,
          contactMethod: contactMethod || undefined,
          summary: summary.trim(),
          notes: notes.trim() || undefined,
          outcome: outcome.trim() || undefined,
          followUpNeeded,
          followUpDate: followUpNeeded && followUpDate ? followUpDate : undefined,
          occurredAt: new Date(occurredAt).toISOString(),
        };
        await onSave(updateData);
      } else {
        const createData: CreateCaseManagementEventRequest = {
          caseId,
          eventType,
          contactMethod: contactMethod || undefined,
          summary: summary.trim(),
          notes: notes.trim() || undefined,
          outcome: outcome.trim() || undefined,
          followUpNeeded,
          followUpDate: followUpNeeded && followUpDate ? followUpDate : undefined,
          occurredAt: new Date(occurredAt).toISOString(),
        };
        await onSave(createData);
      }
      onClose();
    } catch (err: any) {
      console.error("Error saving event:", err);
      const errorMessage = err?.message || err?.details || "Failed to save event. Please try again.";
      setErrors((prev) => ({ ...prev, submit: errorMessage }));
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={isOpen} onClose={onClose} size="lg">
      <DialogTitle>{isEditing ? "Edit Event" : "Add Case Management Event"}</DialogTitle>
      <DialogBody>
        <Fieldset className="space-y-4">
          {/* Submit Error */}
          {errors.submit && (
            <div className="p-3 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg dark:bg-red-900/20 dark:border-red-800 dark:text-red-400">
              {errors.submit}
            </div>
          )}

          {/* Event Type */}
          <Field>
            <Label>Event Type</Label>
            <Select
              value={eventType}
              onChange={(e) => setEventType(e.target.value as CaseManagementEventType)}
              invalid={!!errors.eventType}
            >
              {Object.entries(CASE_MANAGEMENT_EVENT_TYPE_LABELS).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </Select>
            {errors.eventType && <ErrorMessage>{errors.eventType}</ErrorMessage>}
          </Field>

          {/* Contact Method - Only for communication events */}
          {eventType === "client_communication" && (
            <Field>
              <Label>Contact Method</Label>
              <Select
                value={contactMethod}
                onChange={(e) => setContactMethod(e.target.value as ContactMethod)}
                invalid={!!errors.contactMethod}
              >
                <option value="">Select contact method...</option>
                {Object.entries(CONTACT_METHOD_LABELS).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </Select>
              {errors.contactMethod && <ErrorMessage>{errors.contactMethod}</ErrorMessage>}
            </Field>
          )}

          {/* Date/Time */}
          <Field>
            <Label>Date &amp; Time</Label>
            <Input
              type="datetime-local"
              value={occurredAt}
              onChange={(e) => setOccurredAt(e.target.value)}
              invalid={!!errors.occurredAt}
            />
            {errors.occurredAt && <ErrorMessage>{errors.occurredAt}</ErrorMessage>}
          </Field>

          {/* Summary */}
          <Field>
            <Label>Summary</Label>
            <Input
              value={summary}
              onChange={(e) => setSummary(e.target.value)}
              placeholder="Brief description of the event..."
              invalid={!!errors.summary}
            />
            {errors.summary && <ErrorMessage>{errors.summary}</ErrorMessage>}
          </Field>

          {/* Notes */}
          <Field>
            <Label>Notes (Optional)</Label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Additional details..."
              rows={3}
            />
          </Field>

          {/* Outcome */}
          <Field>
            <Label>Outcome (Optional)</Label>
            <Input
              value={outcome}
              onChange={(e) => setOutcome(e.target.value)}
              placeholder="Result or next steps..."
            />
          </Field>

          {/* Follow-up */}
          <div className="space-y-3">
            <CheckboxField>
              <Checkbox
                checked={followUpNeeded}
                onChange={(checked) => setFollowUpNeeded(checked)}
              />
              <Label>Follow-up needed</Label>
            </CheckboxField>

            {followUpNeeded && (
              <Field>
                <Label>Follow-up Date</Label>
                <Input
                  type="date"
                  value={followUpDate}
                  onChange={(e) => setFollowUpDate(e.target.value)}
                />
              </Field>
            )}
          </div>
        </Fieldset>
      </DialogBody>
      <DialogActions>
        <Button outline onClick={onClose} disabled={isSaving}>
          Cancel
        </Button>
        <Button onClick={handleSubmit} disabled={isSaving}>
          {isSaving ? "Saving..." : isEditing ? "Save Changes" : "Add Event"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddCaseManagementEventDialog;
